import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { deriveDisplayName, useAuth } from '../../contexts/AuthContext';
import { fetchAllUsers } from '../../services/authService';
import UserChatHistoryModal from './UserChatHistoryModal';

function formatLastChat(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AllUsersModal({ open, onClose, refreshKey = 0 }) {
  const { user, isSuperAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (!open || !isSuperAdmin) return undefined;

    let cancelled = false;
    setLoading(true);
    setError('');

    fetchAllUsers()
      .then((data) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load users.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isSuperAdmin, refreshKey]);

  useEffect(() => {
    if (!open) setSelectedUser(null);
  }, [open]);

  if (!open || !isSuperAdmin || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div className="admin-dashboard-overlay" onClick={onClose} role="presentation">
        <div
          className="admin-dashboard-modal all-users-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="all-users-title"
          onClick={(event) => event.stopPropagation()}
        >
          <button type="button" className="dashboard-close" onClick={onClose} aria-label="Close all users dialog">
            ×
          </button>

          <div className="admin-dashboard-topbar">
            <div className="admin-dashboard-topbar-copy">
              <span className="dashboard-kicker">Administration</span>
              <h2 id="all-users-title" className="dashboard-title">
                All users
              </h2>
              <p className="dashboard-subtitle">
                Signed in as {user.email}. Click an email to view that user&apos;s chat history.
              </p>
            </div>
            <span className="admin-role-badge">Super Admin</span>
          </div>

          <section className="admin-table-card">
            <div className="admin-table-card-header">
              <h3>Users</h3>
              <span>{rows.length} users</span>
            </div>
            {error ? <div className="dashboard-empty">{error}</div> : null}
            {loading ? <div className="dashboard-empty">Loading users…</div> : null}
            {!loading && !error ? (
              <div className="admin-table-wrap">
                <table className="admin-users-table all-users-table-simple">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Last chat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length ? (
                      rows.map((row) => (
                        <tr key={row.user_id || row.email}>
                          <td>
                            <div className="admin-user-cell">
                              <strong>{row.full_name || deriveDisplayName(row.email)}</strong>
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="all-users-email-link"
                              onClick={() => setSelectedUser(row)}
                            >
                              {row.email}
                            </button>
                          </td>
                          <td>{formatLastChat(row.last_chat_at ?? row.lastChatAt)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3">No users found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>
        </div>
      </div>

      <UserChatHistoryModal
        open={Boolean(selectedUser)}
        user={selectedUser}
        onClose={() => setSelectedUser(null)}
      />
    </>,
    document.body
  );
}
